import { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';

type Props = {
  show: boolean;
  iOwnTheAccount?: string;
  iNeedAccessToView?: string;
  onClose: () => void;
  candidateName?: string;
  candidateUsername?: string;
  initialEmail?: string;
};

export default function RecruiterStepModal({
  show,
  onClose,
  candidateName = 'Candidate',
  candidateUsername = '',
  initialEmail = '',
}: Props) {
  const [step, setStep] = useState<number>(1);
  const [role, setRole] = useState<'recruiter' | 'hiring-manager' | 'other'>('recruiter');
  const [email, setEmail] = useState<string>(initialEmail);
  const [allowCandidate, setAllowCandidate] = useState<boolean>(true);
  const [company, setCompany] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [infoMessage, setInfoMessage] = useState<string>('');
  const [recruiterVerified, setRecruiterVerified] = useState<boolean>(false);

  useEffect(() => {
    if (!show) {
      setStep(1);
      setRole('recruiter');
      setEmail(initialEmail);
      setAllowCandidate(true);
      setCompany('');
      setLoading(false);
      setError('');
      setInfoMessage('');
      setRecruiterVerified(false);
    }
  }, [show, initialEmail]);

  const maskEmail = (em: string) => {
    if (!em.includes('@')) return em;
    const [local, domain] = em.split('@');
    if (local.length <= 2) return '*'.repeat(local.length) + '@' + domain;
    return local[0] + '*'.repeat(local.length - 2) + local[local.length - 1] + '@' + domain;
  };

  // Add derived state for company input
  const showCompanyInput = role === 'other';

  const sendGetAccess = async () => {
    setError('');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    // 👇 Skip API if role is "other"
    if (role === 'other') {
      if (!company || company.trim().length < 2) {
        setError('Please enter your company name');
        return;
      }
      // forcing accessor to be recruiter
      setRole('recruiter');
      // setInfoMessage('Verification link sent. Please check your email.');
      // setStep(2);
      // return;
    }

    // For recruiter / hiring-manager → use API
    setLoading(true);
    try {
      const res = await axiosInstance.post(API_ROUTES.AI.FIND_COMPANY, {
        email,
      });

      console.log('Find company response:', res.data);

      if (res.data.result === 'company' && res.data.name) {
        setCompany(res.data.name);
        setInfoMessage(`Verification link sent to your company email at ${res.data.name}.`);
        // send api call to request access
        const data = {
          email,
          company: res.data.name,
          allowCandidate,
          role,
          username: candidateUsername,
        };

        console.log('Request access data:', data);
        const access = await axiosInstance.post(API_ROUTES.RECRUITER.REQUEST_ACCESS, data);
        if (access.status === 200) {
          setStep(2);
        } else {
          setError(
            access.data.message || 'Failed to send verification link. Please try again later.'
          );
        }
      } else {
        setInfoMessage('Domain not recognized. Please enter your company manually.');
        setStep(3);
      }
    } catch (err: any) {
      console.error('Find company error:', err);
      setError(err?.response?.data?.message || 'Failed to send verification link');
    } finally {
      setLoading(false);
    }
  };

  const proceedNonCompany = () => {
    if (!company || company.trim().length < 2) {
      setError('Enter Company Name');
      return;
    }
    setInfoMessage('Verification link sent. Please check your email.');
    setStep(2);
  };

  // const confirmVerification = () => {
  //   setRecruiterVerified(true);
  //   setStep(4);
  // };

  const recruiterLogin = () => {
    window.open('/', '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Body className="bg-light text-center p-4">
        <div className="d-inline-block bg-success text-white fw-bold fs-4 py-2 px-5 rounded-pill shadow mb-3">
          Are you a
        </div>

        {/* STEP 1: Select role + email */}
        {step === 1 && (
          <>
            {infoMessage && <Alert variant="success">{infoMessage}</Alert>}
            <Form className="text-start px-3">
              <div className="mb-2">
                <Form.Check
                  type="radio"
                  id="recruiter"
                  label="Recruiter"
                  checked={role === 'recruiter'}
                  onChange={() => setRole('recruiter')}
                />
                <Form.Check
                  type="radio"
                  id="hiring-manager"
                  label="Hiring Manager"
                  checked={role === 'hiring-manager'}
                  onChange={() => setRole('hiring-manager')}
                />
                <Form.Check
                  type="radio"
                  id="other"
                  label="Others"
                  checked={role === 'other'}
                  onChange={() => setRole('other')}
                />
              </div>

              <Form.Control
                type="email"
                placeholder="john.doe@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-pill bg-opacity-75 fw-semibold text-dark"
              />

              {/* 👇 Only show if role = other */}
              {showCompanyInput && (
                <Form.Control
                  type="text"
                  placeholder="Enter Company Name"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="rounded-pill mt-2"
                />
              )}

              <Form.Check
                className="mt-3"
                type="checkbox"
                label={`Allow ${candidateName} to see my email`}
                checked={allowCandidate}
                onChange={(e) => setAllowCandidate(e.target.checked)}
              />
            </Form>
            {error && <div className="text-danger mt-2 fw-bold">{error}</div>}
          </>
        )}

        {/* STEP 2: Magic link sent */}
        {step === 2 && (
          <>
            <Alert variant="success">{infoMessage}</Alert>
            {/* <div className="my-3">
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="54" fill="#2ef04b" />
                <path
                  d="M38 62 l18 18 l34 -38"
                  stroke="#062"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </div>
            <Button
              variant="success"
              className="rounded-pill fw-bold px-4 mt-3"
              onClick={confirmVerification}
            >
              I clicked the link
            </Button> */}
            <Button variant="success" className="rounded-pill fw-bold px-4" onClick={onClose}>
              Done
            </Button>
          </>
        )}

        {/* STEP 3: Manual company entry */}
        {step === 3 && (
          <>
            <Alert variant="warning">{infoMessage}</Alert>
            <Form className="px-3">
              <Form.Control
                type="text"
                value={allowCandidate ? email : maskEmail(email)}
                disabled
                className="rounded-pill mb-2"
              />
              <Form.Check
                type="checkbox"
                label={`Allow ${candidateName} to see my email`}
                checked={allowCandidate}
                onChange={(e) => setAllowCandidate(e.target.checked)}
                className="mb-2"
              />
              <Form.Control
                type="text"
                placeholder="Enter Company Name"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="rounded-pill"
              />
            </Form>
            {error && <div className="text-danger mt-2 fw-bold">{error}</div>}
          </>
        )}

        {/* STEP 4: Verified recruiter full access */}
        {step === 4 && recruiterVerified && (
          <>
            <Alert variant="success">
              Recruiter verified! You now temporary have full access to {candidateName}'s profile.
              Please, check your email for the authenticated profile link.
            </Alert>
            <div className="mt-3">
              <p>Portfolio, projects, accomplishments</p>
              <p>Messaging & questions</p>
              <p>Calendar (when available)</p>
            </div>
            <div className="alert alert-info mt-3">
              SkipScreen Recruiter Profiles are coming soon. <br />
              <strong>Join the waitlist</strong> to unlock advanced recruiter features.
            </div>
          </>
        )}

        {/* ACTION BUTTONS */}
        <div className="d-flex justify-content-center gap-3 mt-4">
          <Button variant="dark" className="rounded-pill fw-bold px-4" onClick={recruiterLogin}>
            Recruiter Login
          </Button>

          {step === 1 && (
            <Button
              variant="success"
              className="rounded-pill fw-bold px-4"
              onClick={sendGetAccess}
              disabled={loading}
            >
              {loading ? '...' : 'Get Access'}
            </Button>
          )}

          {step === 3 && (
            <Button
              variant="success"
              className="rounded-pill fw-bold px-4"
              onClick={proceedNonCompany}
              disabled={loading}
            >
              {loading ? '...' : 'Proceed'}
            </Button>
          )}

          {step === 4 && (
            <Button variant="success" className="rounded-pill fw-bold px-4" onClick={onClose}>
              Done
            </Button>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
